import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NewTicketForm from '../../components/tickets/NewTicketForm';
import { ticketService } from '../../services/ticket';
import { useTheme } from '../../context/ThemeContext';

const NewTicketPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const { isDark } = useTheme();

  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      setError(null);
    }, 4000);

    return () => clearTimeout(timer);
  }, [error]);

  const handleSubmit = async (newTicket) => {
    try {
      setError(null);
      await ticketService.createTicket(newTicket);
      navigate('/tickets');
    } catch (err) {
      setError(
        err?.detail ||
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to create ticket'
      );
    }
  };

  return (
    <div className="min-h-screen py-2 sm:py-4 px-0 sm:px-4 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="shadow-sm sm:rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6">
            {error && (
              <div
                role="alert"
                aria-live="assertive"
                className={`fixed top-5 right-5 z-[100] w-[calc(100%-2rem)] max-w-sm
                  rounded-xl border p-4 shadow-2xl
                  animate-[slideIn_0.3s_ease-out]
                  ${
                    isDark
                      ? 'bg-gray-900 border-red-800 text-gray-100'
                      : 'bg-white border-red-200 text-gray-800'
                  }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3m0 4h.01M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.7 3.86a2 2 0 00-3.4 0z"
                      />
                    </svg>
                  </div>

                  <div className="min-w-0 flex-1 pt-0.5">
                    <h3 className="text-sm font-semibold text-red-600">
                      Unable to Create Ticket
                    </h3>
                    <p className="mt-1 text-sm leading-5 opacity-90 break-words">
                      {typeof error === 'string'
                        ? error
                        : 'Something went wrong. Please try again.'}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setError(null)}
                    aria-label="Close notification"
                    className={`shrink-0 rounded-md p-1 transition
                      ${
                        isDark
                          ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                          : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
                      }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="mt-3 h-1 overflow-hidden rounded-full bg-red-100">
                  <div className="h-full w-full origin-left animate-[shrink_4s_linear_forwards] bg-red-500" />
                </div>
              </div>
            )}

            <NewTicketForm
              onSubmit={handleSubmit}
              onCancel={() => navigate('/tickets')}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes shrink {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default NewTicketPage;